import { useEffect, useState } from 'react';
import { isDebugUIEnabled } from '../../config/debug';
import { useDebugPhase } from '../../context/DebugPhaseContext';
import { useEndSessionDebug } from '../../context/EndSessionDebugContext';
import { useVoiceActivityContext } from '../../context/VoiceActivityContext';
import useMuteState from '../../hooks/useMuteState';
import useParticipantsWithHost from '../../hooks/useParticipantsWithHost';
import useWSMessageSender from '../../hooks/useWSMessageSender';
import useWSTurnState, { TURN_ACTIONS, TURN_PHASES } from '../../hooks/useWSTurnState';
import { getDebugVisibility, setDebugVisibility, subscribeDebugVisibility } from '../../utils/debugToggleState';
import ApiDebugSection from './ApiDebugSection';
import './StateDebugPanel.css';
import VoiceActivityDebug from './VoiceActivityDebug';

export default function StateDebugPanel() {
  if (!isDebugUIEnabled) {
    return null;
  }

  const { currentPhase: wsPhase, lastCompleter } = useWSTurnState();
  const { debugPhase } = useDebugPhase();
  const currentPhase = isDebugUIEnabled ? debugPhase : wsPhase;
  const { sendTurnAction } = useWSMessageSender();
  const { whoAmI } = useParticipantsWithHost();
  const { isMuted } = useMuteState();
  const voiceState = useVoiceActivityContext();
  const { isFirstSession, setIsFirstSession, debugGoodbyeDone, setDebugGoodbyeDone } = useEndSessionDebug();
  const [isVisible, setIsVisible] = useState(getDebugVisibility());

  useEffect(() => {
    const unsubscribe = subscribeDebugVisibility(setIsVisible);

    const handleKeyPress = (event) => {
      if (event.code === 'Backquote') {
        const { target } = event;
        const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;

        if (!isInput) {
          event.preventDefault();
          setDebugVisibility(!getDebugVisibility());
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress, true);

    return () => {
      unsubscribe();
      window.removeEventListener('keydown', handleKeyPress, true);
    };
  }, []);

  return (
    <div style={{ display: isVisible ? 'block' : 'none' }} className="state-debug-panel">
      {/* Header */}
      <div className="debug-header">
        <span className="debug-title">🎮 State Debug</span>
        <span className="debug-whoami">Você é: <strong>{whoAmI || '?'}</strong></span>
      </div>

      {/* Estados Atuais */}
      <div className="debug-states">
        <div className="state-item">
          <span className="state-label">Turn Phase:</span>
          <span className="state-value">{currentPhase}</span>
        </div>
        <div className="state-item">
          <span className="state-label">Last Completer:</span>
          <span className="state-value">{lastCompleter || 'None'}</span>
        </div>
        <div className="state-item">
          <span className="state-label">Mute Status:</span>
          <span className="state-value">{isMuted ? '🔇 MUTED' : '🎤 UNMUTED'}</span>
        </div>
      </div>

      {/* Ações Turn State — ocultas nas fases de encerramento */}
      {currentPhase !== TURN_PHASES.Goodbye && currentPhase !== TURN_PHASES.Checkout && (
        <>
          <div className="debug-actions">
            <div className="actions-group">
              <span className="actions-label">Turn Actions:</span>
              <div className="actions-buttons">
                <button
                  type="button"
                  onClick={() => sendTurnAction({
                    action: TURN_ACTIONS.ACompleted,
                    spokenContinuation: "debug test phrase for A"
                  })}
                  className="action-btn action-btn-green"
                >
                  A Done
                </button>
                <button
                  type="button"
                  onClick={() => sendTurnAction({
                    action: TURN_ACTIONS.BCompleted,
                    spokenContinuation: "debug test phrase for B"
                  })}
                  className="action-btn action-btn-green"
                >
                  B Done
                </button>
                <button
                  type="button"
                  onClick={() => sendTurnAction({
                    action: TURN_ACTIONS.AAsked,
                    spokenContinuation: "debug ask from A"
                  })}
                  className="action-btn action-btn-blue"
                >
                  A Ask
                </button>
                <button
                  type="button"
                  onClick={() => sendTurnAction({
                    action: TURN_ACTIONS.BAsked,
                    spokenContinuation: "debug ask from B"
                  })}
                  className="action-btn action-btn-blue"
                >
                  B Ask
                </button>
                <button
                  type="button"
                  onClick={() => sendTurnAction({
                    action: TURN_ACTIONS.AwkwardSilence,
                    spokenContinuation: "debug awkward silence"
                  })}
                  className="action-btn action-btn-yellow"
                >
                  Silence
                </button>
              </div>
            </div>
          </div>

          {/* Voice Activity Debug */}
          <VoiceActivityDebug voiceState={voiceState} />

          <ApiDebugSection />
        </>
      )}

      {/* Checkout Debug — aparece quando phase === GOODBYE ou CHECKOUT */}
      {(currentPhase === TURN_PHASES.Goodbye || currentPhase === TURN_PHASES.Checkout) && (
        <div className="debug-section">
          <span className="debug-section-title">🏁 Checkout</span>
          <div className="state-item" style={{ cursor: 'pointer' }} onClick={() => setIsFirstSession(!isFirstSession)}>
            <span className="state-label">1ª sessão:</span>
            <input type="checkbox" checked={isFirstSession} onChange={() => setIsFirstSession(!isFirstSession)} />
          </div>
          {currentPhase === TURN_PHASES.Goodbye && (
            <div className="state-item" style={{ cursor: 'pointer' }} onClick={() => setDebugGoodbyeDone(!debugGoodbyeDone)}>
              <span className="state-label">eu já cliquei:</span>
              <input type="checkbox" checked={debugGoodbyeDone} onChange={() => setDebugGoodbyeDone(!debugGoodbyeDone)} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

